// src/contexts/PaymentContext.js

import React, { createContext, useContext, useState, useCallback } from 'react';

const PaymentContext = createContext();

export const PaymentProvider = ({ children }) => {
  const [paymentData, setPaymentData] = useState({
    provider: null, // 'mtn' ou 'orange'
    phone: '',
    amount: 0,
    ticketType: null, // 'standard' ou 'vip'
    transactionId: null,
    status: 'idle', // 'idle', 'processing', 'success', 'failed'
    paymentId: null, // ID du paiement en BDD
    error: null
  });

  // Initialiser un nouveau paiement
  const initializePayment = useCallback((ticketType, amount) => {
    setPaymentData({
      provider: null,
      phone: '',
      amount,
      ticketType,
      transactionId: null,
      status: 'idle',
      paymentId: null,
      error: null
    });
  }, []);

  // Définir le provider (MTN ou Orange)
  const setProvider = useCallback((provider) => {
    setPaymentData(prev => ({
      ...prev,
      provider,
      error: null
    }));
  }, []);

  // Définir le numéro de téléphone
  const setPhone = useCallback((phone) => {
    setPaymentData(prev => ({
      ...prev,
      phone,
      error: null
    }));
  }, []);

  // Mettre à jour le statut du paiement
  const updatePaymentStatus = useCallback((status, data = {}) => {
    setPaymentData(prev => ({
      ...prev,
      status,
      ...data
    }));
  }, []);

  // Définir une erreur
  const setError = useCallback((error) => {
    setPaymentData(prev => ({
      ...prev,
      error,
      status: 'failed'
    }));
  }, []);

  // Réinitialiser le paiement
  const resetPayment = useCallback(() => {
    setPaymentData({
      provider: null,
      phone: '',
      amount: 0,
      ticketType: null,
      transactionId: null,
      status: 'idle',
      paymentId: null,
      error: null
    });
  }, []);

  // Valider si le paiement peut être soumis
  const canSubmitPayment = useCallback(() => {
    const { provider, phone } = paymentData;
    
    // Vérifier que le provider est sélectionné
    if (!provider) return false;
    
    // Vérifier que le téléphone est valide (9 chiffres, commence par 6)
    const phoneRegex = /^6[5-9]\d{7}$/;
    if (!phoneRegex.test(phone)) return false;
    
    return true;
  }, [paymentData]);

  const value = {
    paymentData,
    initializePayment,
    setProvider,
    setPhone,
    updatePaymentStatus,
    setError,
    resetPayment,
    canSubmitPayment
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte
export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment doit être utilisé dans un PaymentProvider');
  }
  return context;
};

export default PaymentContext;